import { toYearMonth } from '../../utils/dateUtils.js'

function periodKeyFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function findNearestIndexValue(serie = {}, key = '') {
  if (!serie || typeof serie !== 'object') return 0
  const exact = Number(serie[key] || 0)
  if (exact > 0) return exact
  const keys = Object.keys(serie).filter((k) => /^\d{4}-\d{2}$/.test(String(k))).sort()
  if (!keys.length) return 0
  const prev = keys.filter((k) => k <= key).pop()
  if (prev) return Number(serie[prev] || 0)
  return Number(serie[keys[0]] || 0)
}

function getOrderedKeysInRange(serie = {}, fromKey = '', toKey = '') {
  return Object.keys(serie)
    .filter((k) => /^\d{4}-\d{2}$/.test(String(k)))
    .filter((k) => k >= fromKey && k <= toKey)
    .sort()
}

function inferSerieKind(serie = {}) {
  const values = Object.values(serie)
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b)
  if (!values.length) return 'unknown'
  const mid = values[Math.floor(values.length / 2)]
  return mid <= 10 ? 'rate' : 'index'
}

function computeFactorFromRateSerie(serie = {}, fromKey = '', toKey = '') {
  const keys = getOrderedKeysInRange(serie, fromKey, toKey)
  if (!keys.length) return 1
  let factor = 1
  for (const key of keys) {
    const rate = Number(serie[key])
    if (!Number.isFinite(rate)) continue
    if (rate <= -0.9 || rate > 2.5) continue
    factor *= (1 + rate)
  }
  return Number.isFinite(factor) && factor > 0 ? factor : 1
}

function computeFactorFromIndexSerie(serie = {}, fromKey = '', toKey = '') {
  const fromValue = findNearestIndexValue(serie, fromKey)
  const toValue = findNearestIndexValue(serie, toKey)
  if (!(fromValue > 0 && toValue > 0)) return 1
  const factor = toValue / fromValue
  return Number.isFinite(factor) && factor > 0 ? factor : 1
}

export function buildMovilidadDesdeCese(fechaCese, fechaCorte, indicesMovilidad = {}) {
  const ceseDate = new Date(fechaCese)
  const corteDateRaw = fechaCorte ? new Date(fechaCorte) : new Date()
  const corteDate = Number.isNaN(corteDateRaw.getTime()) ? new Date() : corteDateRaw
  if (Number.isNaN(ceseDate.getTime())) {
    return { factor: 1, tramoAplicado: 'Sin fecha de cese valida para movilidad acumulada', referencia: 'none' }
  }
  const ceseMonth = new Date(ceseDate.getFullYear(), ceseDate.getMonth(), 1)
  const corteMonth = new Date(corteDate.getFullYear(), corteDate.getMonth(), 1)
  if (ceseMonth >= corteMonth) {
    return { factor: 1, tramoAplicado: 'Sin movilidad adicional por cese cercano a fecha de corte', referencia: 'none' }
  }

  const ipcSerie = indicesMovilidad?.IPC?.serie || {}
  const ripteSerie = indicesMovilidad?.RIPTE?.serie || {}
  const seriePrincipal = Object.keys(ipcSerie).length > 0 ? ipcSerie : ripteSerie
  const referencia = Object.keys(ipcSerie).length > 0 ? 'IPC (incluye esquema DNU 274/24)' : 'RIPTE (fallback)'
  const kCese = periodKeyFromDate(ceseMonth)
  const kCorte = periodKeyFromDate(corteMonth)
  const serieKind = inferSerieKind(seriePrincipal)
  const factorByIndex = computeFactorFromIndexSerie(seriePrincipal, kCese, kCorte)
  const factorByRate = computeFactorFromRateSerie(seriePrincipal, kCese, kCorte)
  let factor = serieKind === 'rate' ? factorByRate : factorByIndex
  if (!Number.isFinite(factor) || factor <= 0 || factor < 0.5 || factor > 20) {
    factor = factorByRate > 0.5 && factorByRate < 20 ? factorByRate : 1
  }

  const periodos = [
    { desde: '2009-01', hasta: '2017-12', norma: 'Ley 26.417' },
    { desde: '2018-01', hasta: '2019-12', norma: 'Ley 27.426' },
    { desde: '2020-01', hasta: '2020-12', norma: 'Decretos 2020 (Caliva)' },
    { desde: '2021-01', hasta: '2024-03', norma: 'Ley 27.609' },
    { desde: '2024-04', hasta: '2099-12', norma: 'DNU 274/24 (IPC)' },
  ]
  const ceseKeyNum = toYearMonth(kCese)
  const corteKeyNum = toYearMonth(kCorte)
  const normas = periodos
    .filter((p) => toYearMonth(p.hasta) >= ceseKeyNum && toYearMonth(p.desde) <= corteKeyNum)
    .map((p) => p.norma)

  return {
    factor: Number.isFinite(factor) && factor > 0 ? factor : 1,
    tramoAplicado: normas.length ? normas.join(' + ') : 'Escala general por serie mensual',
    referencia,
  }
}

export function getRipdocFactor(fechaCese, fechaCorte, seriesOficiales = {}) {
  const ceseDate = new Date(fechaCese)
  const corteDateRaw = fechaCorte ? new Date(fechaCorte) : new Date()
  const corteDate = Number.isNaN(corteDateRaw.getTime()) ? new Date() : corteDateRaw
  if (Number.isNaN(ceseDate.getTime())) return 1
  const fromKey = `${ceseDate.getFullYear()}-${String(ceseDate.getMonth() + 1).padStart(2, '0')}`
  const toKey = `${corteDate.getFullYear()}-${String(corteDate.getMonth() + 1).padStart(2, '0')}`
  const serie = seriesOficiales?.MOVILIDAD_RIPDOC?.mensual || {}
  const keys = Object.keys(serie).sort()
  if (!keys.length) return 1
  let factor = 1
  keys.forEach((k) => {
    if (k >= fromKey && k <= toKey) factor *= (1 + Number(serie[k] || 0))
  })
  return Number.isFinite(factor) && factor > 0 ? factor : 1
}
